import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import api from '../api';
import { useTwitchAuth } from '../context/TwitchAuthContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import DualLinkModal from './DualLinkModal';

function navLinkClass({ isActive }: { isActive: boolean }) {
  return [
    'text-sm whitespace-nowrap transition-colors',
    isActive ? 'text-rose-300 font-medium' : 'text-slate-400 hover:text-rose-200',
  ].join(' ');
}

function StatusPill({ on, children, title }: { on: boolean; children: React.ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className={[
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide flex-shrink-0',
        on
          ? 'text-emerald-300 bg-emerald-400/15 border border-emerald-400/30'
          : 'text-rose-200 bg-rose-400/10 border border-rose-400/25',
      ].join(' ')}
    >
      {children}
    </span>
  );
}

const PublicLayout: React.FC = () => {
  const location = useLocation();
  const { user, token, logout } = useTwitchAuth();
  const { auctionsPublicEnabled, oracleClaimsEnabled, oracleMaxClaimsPerStream, loading } = useSiteConfig();
  const [linkOpen, setLinkOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    if (!token) {
      setComplete(false);
      return;
    }
    api
      .get('/discord/auth/status')
      .then(({ data }) => setComplete(!!data.complete))
      .catch(() => setComplete(false));
  }, [token, user, linkOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const openLogin = () => {
    setLinkOpen(true);
    setMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0815] text-white antialiased">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0a0815]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3 py-3.5">
            <Link
              to="/"
              className="text-lg sm:text-xl font-bold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-rose-300 to-sky-300 flex-shrink-0"
            >
              EUGENIAL
            </Link>

            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border border-white/10 bg-white/5 text-white"
              aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
              onClick={() => setMenuOpen((v) => !v)}
            >
              {menuOpen ? '✕' : '☰'}
            </button>

            <nav
              className={[
                'w-full lg:w-auto lg:flex lg:items-center lg:gap-5 lg:ml-auto',
                menuOpen ? 'flex flex-col gap-3 pt-2 border-t border-white/10 lg:border-0 lg:pt-0' : 'hidden lg:flex',
              ].join(' ')}
            >
              <NavLink to="/" end className={navLinkClass} onClick={() => setMenuOpen(false)}>
                Inicio
              </NavLink>
              {!loading && auctionsPublicEnabled && (
                <NavLink to="/subastas" className={navLinkClass} onClick={() => setMenuOpen(false)}>
                  Subastas
                </NavLink>
              )}
              {!loading && (
                <StatusPill on={oracleClaimsEnabled} title={`Máx. ${oracleMaxClaimsPerStream} claims por directo`}>
                  Oráculos {oracleClaimsEnabled ? 'ON' : 'OFF'}
                </StatusPill>
              )}

              <div className="flex flex-col sm:flex-row sm:items-center gap-2 lg:ml-2 pt-2 lg:pt-0 border-t border-white/10 lg:border-0">
                {user ? (
                  <>
                    <span className="text-xs sm:text-sm text-slate-400 truncate max-w-[160px]" title={user.displayName}>
                      {user.displayName}
                      {!complete ? ' · falta Discord' : ''}
                    </span>
                    {!complete && (
                      <button
                        type="button"
                        onClick={openLogin}
                        className="px-3 py-1.5 rounded-xl text-sm font-medium bg-gradient-to-r from-rose-400/90 to-pink-500/90 text-white hover:opacity-90 transition-opacity"
                      >
                        Vincular Discord
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => { logout(); setMenuOpen(false); }}
                      className="px-3 py-1.5 rounded-xl text-sm border border-white/15 text-slate-300 hover:text-white hover:border-white/30 transition-colors"
                    >
                      Salir
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={openLogin}
                    className="px-4 py-1.5 rounded-xl text-sm font-medium bg-gradient-to-r from-rose-400/90 to-pink-500/90 text-white hover:opacity-90 transition-opacity"
                  >
                    Entrar
                  </button>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>

      {!loading && (
        <div className="border-b border-rose-400/15 bg-rose-400/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm text-slate-400">
            <span>
              {oracleClaimsEnabled
                ? `Claims abiertos · hasta ${oracleMaxClaimsPerStream} por directo.`
                : 'Claims cerrados · solo consultas (!oraculo).'}
            </span>
            <StatusPill on={oracleClaimsEnabled}>{oracleClaimsEnabled ? 'ON' : 'OFF'}</StatusPill>
          </div>
        </div>
      )}

      {user && !complete && (
        <div className="border-b border-white/10 bg-white/[0.03]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm text-slate-400">
            <span>Completá tu cuenta vinculando Twitch y Discord.</span>
            <button
              type="button"
              onClick={openLogin}
              className="px-3 py-1.5 rounded-xl text-sm font-medium bg-white/10 hover:bg-white/15 text-white transition-colors"
            >
              Completar vínculo
            </button>
          </div>
        </div>
      )}

      <main>
        <Outlet />
      </main>

      <DualLinkModal open={linkOpen} onClose={() => setLinkOpen(false)} />
    </div>
  );
};

export default PublicLayout;
